import { useEffect } from "react";
import Pica from "pica";

const helper_functions = {
  pixel_To_percent(x, y, CW, CH) {
    let Px = (x / CW) * 100;
    let Py = (y / CH) * 100;
    return {
      x: Px,
      y: Py,
    };
  },
  percent_To_pixel(x, y, CW, CH) {
    let Px = (x / 100) * CW;
    let Py = (y / 100) * CH;
    return {
      x: Px,
      y: Py,
    };
  },
  useDebounceEffect(fn, waitTime, deps) {
    useEffect(() => {
      const t = setTimeout(() => {
        fn.apply(undefined, deps);
      }, waitTime);

      return () => {
        clearTimeout(t);
      };
    }, deps);
  },
  degToRad(angle) {
    return (angle / 180) * Math.PI;
  },
  newPoints(centerx, centery, Px, Py, degree) {
    let x =
      centerx +
      (Px - centerx) * Math.cos(degree) -
      (Py - centery) * Math.sin(degree);
    let y =
      centery +
      (Px - centerx) * Math.sin(degree) +
      (Py - centery) * Math.cos(degree);
    return {
      centerx: centerx,
      centery: centery,
      oldx: Px,
      oldy: Py,
      X: x,
      Y: y,
    };
  },
  alignment(
    property,
    imageProperties,
    shapeRef,
    degToRad,
    getCenterPoint,
    yCordsOfMainImage,
    xCordsOfMainImage,
    differenceFactor,
    canvasImageHeight,
    canvasImageWidth,
    setYCords,
    setXCords,
    newPoints
  ) {
    if (imageProperties.hasOwnProperty("alignEnable") && shapeRef.current) {
      let degree = imageProperties.deg;
      let deg = degToRad(degree);
      let xOld, yOld, centerx, centery, width, height;
      width = property
        ? shapeRef.current.width() * imageProperties.scaleX * differenceFactor
        : imageProperties.image.width * imageProperties.scaleX;
      height = property
        ? shapeRef.current.height() * imageProperties.scaleY * differenceFactor
        : imageProperties.image.height * imageProperties.scaleY;
      centerx = xCordsOfMainImage + width / 2;
      centery = yCordsOfMainImage + height / 2;

      switch (imageProperties.alignEnable) {
        case "TopLeft":
          xOld = xCordsOfMainImage;
          yOld = yCordsOfMainImage;
          break;
        case "TopRight":
          xOld = canvasImageWidth - width;
          yOld = yCordsOfMainImage;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        case "BottomLeft":
          xOld = xCordsOfMainImage;
          yOld = canvasImageHeight - height + yCordsOfMainImage;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        case "BottomRight":
          xOld = canvasImageWidth - width;
          yOld = canvasImageHeight - height + yCordsOfMainImage;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        case "TopCenter":
          xOld = canvasImageWidth / 2 - width / 2;
          yOld = yCordsOfMainImage;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        case "BottomCenter":
          xOld = canvasImageWidth / 2 - width / 2;
          yOld = canvasImageHeight - height + yCordsOfMainImage;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        default:
          break;
      }

      let yOldPL = yOld + height;
      let xOldPR = xOld + width;
      let xOldPB = xOld + width;
      let yOldPB = yOld + height;
      let newPT = newPoints(centerx, centery, xOld, yOld, deg);
      let newPR = newPoints(centerx, centery, xOldPR, yOld, deg);
      let newPB = newPoints(centerx, centery, xOldPB, yOldPB, deg);
      let newPL = newPoints(centerx, centery, xOld, yOldPL, deg);

      let newX = 0;
      let newY = 0;

      switch (imageProperties.alignEnable) {
        case "TopLeft":
          newX =
            Math.min(newPL.X, newPT.X, newPR.X, newPB.X) - xCordsOfMainImage;
          newY =
            Math.min(newPL.Y, newPT.Y, newPR.Y, newPB.Y) - yCordsOfMainImage;
          setXCords(
            -1 * newX +
              xCordsOfMainImage +
              getCenterPoint().x * imageProperties.scaleX
          );
          setYCords(
            -1 * newY +
              yCordsOfMainImage +
              getCenterPoint().y * imageProperties.scaleY
          );
          break;
        case "TopRight":
          newX =
            Math.max(newPL.X, newPT.X, newPR.X, newPB.X) -
            canvasImageWidth -
            xCordsOfMainImage;
          newY =
            Math.min(newPL.Y, newPT.Y, newPR.Y, newPB.Y) - yCordsOfMainImage;
          setXCords(
            -1 * newX + xOld + getCenterPoint().x * imageProperties.scaleX
          );
          setYCords(
            -1 * newY +
              yCordsOfMainImage +
              getCenterPoint().y * imageProperties.scaleY
          );
          break;

        case "BottomLeft":
          newX =
            Math.min(newPL.X, newPT.X, newPR.X, newPB.X) - xCordsOfMainImage;
          newY =
            Math.max(newPL.Y, newPT.Y, newPR.Y, newPB.Y) -
            canvasImageHeight -
            yCordsOfMainImage;
          setXCords(
            -1 * newX +
              xCordsOfMainImage +
              getCenterPoint().x * imageProperties.scaleX
          );
          setYCords(
            -1 * newY + yOld + getCenterPoint().y * imageProperties.scaleY
          );
          break;

        case "BottomRight":
          newX =
            Math.max(newPL.X, newPT.X, newPR.X, newPB.X) -
            canvasImageWidth -
            xCordsOfMainImage;
          newY =
            Math.max(newPL.Y, newPT.Y, newPR.Y, newPB.Y) -
            canvasImageHeight -
            yCordsOfMainImage;
          setXCords(
            -1 * newX + xOld + getCenterPoint().x * imageProperties.scaleX
          );
          setYCords(
            -1 * newY + yOld + getCenterPoint().y * imageProperties.scaleY
          );
          break;
        case "TopCenter":
          newY =
            Math.min(newPL.Y, newPT.Y, newPR.Y, newPB.Y) - yCordsOfMainImage;
          setXCords(
            xOld +
              xCordsOfMainImage +
              getCenterPoint().x * imageProperties.scaleX
          );
          setYCords(
            -1 * newY + yOld + getCenterPoint().y * imageProperties.scaleY
          );

          break;
        case "BottomCenter":
          newY =
            Math.max(newPL.Y, newPT.Y, newPR.Y, newPB.Y) -
            canvasImageHeight -
            yCordsOfMainImage;
          setXCords(
            xOld +
              xCordsOfMainImage +
              getCenterPoint().x * imageProperties.scaleX
          );
          setYCords(
            -1 * newY + yOld + getCenterPoint().y * imageProperties.scaleY
          );
          break;
        case "Center":
          setXCords(
            canvasImageWidth / 2 +
              xCordsOfMainImage -
              width / 2 +
              getCenterPoint().x * imageProperties.scaleX
          );
          setYCords(
            canvasImageHeight / 2 +
              yCordsOfMainImage -
              height / 2 +
              getCenterPoint().y * imageProperties.scaleY
          );
          break;
        default:
          break;
      }
    }
  },
  dataURLtoFile(dataurl, filename) {
    if (dataurl) {
      var arr = dataurl.split(","),
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new File([u8arr], filename);
    }
  },
  blobToFile(blob, fileName) {
    const file = new File([blob], fileName, { type: blob.type });
    return file;
  },
  convertToDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  },
  blobToDataURL(blob) {
    var reader = new FileReader();
    reader.onloadend = function () {
      return reader.result;
    };
    reader.readAsDataURL(blob);
  },
  WebToFile(url) {
    return fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const filename = url.substring(url.lastIndexOf("/") + 1);
        const imageFile = new File([blob], filename, { type: blob.type });
        return imageFile;
      });
  },
  isImageExtension(filename) {
    const imageExtensions = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "tiff",
      "svg",
      "webp",
      "ico",
      "jfif",
    ];
    const ext = filename.split(".").pop().toLowerCase();
    return imageExtensions.includes(ext) ? ext : false;
  },
  decryptToScaleAndSize(widthPercentage, canvasWidth, originalSize) {
    const imageWidthOnCanvas = widthPercentage * canvasWidth;
    const scale = imageWidthOnCanvas / originalSize;
    return scale;
  },
  encryptToWidthPercentage(
    scaleX,
    scaleY,
    imagewidth,
    imageHeight,
    canvasWidth,
    xPoint,
    yPoint
  ) {
    const imageWidthOnCanvas = (imagewidth * scaleX) / canvasWidth;
    const imageheightOnCanvas = (imageHeight * scaleY) / canvasWidth;
    let newCordsPx = helper_functions.percent_To_pixel(
      xPoint,
      yPoint,
      canvasWidth,
      canvasWidth
    );

    const x = newCordsPx.x - (imagewidth * scaleX) / 2;
    const y = newCordsPx.y - (imageHeight * scaleY) / 2;
    let newCordsPc = helper_functions.pixel_To_percent(
      x,
      y,
      canvasWidth,
      canvasWidth
    );

    return {
      imageWidthOnCanvas,
      imageheightOnCanvas,
      x: newCordsPc.x,
      y: newCordsPc.y,
    };
  },
  resetOffset(x, y, width, height) {
    const newX = x + width / 2;
    const newY = y + (height * width) / 2;

    return {
      x: newX,
      y: newY,
    };
  },
  dataWrapperEncrypt(data, tag, canvasSize) {
    let size = this.encryptToWidthPercentage(
      data.scaleX,
      data.scaleY,
      data.containerWidth,
      data.containerHight,
      canvasSize,
      data.x,
      data.y
    );

    var dataReturn = {
      type: data.type, /// image , text
      sub_type: data.property, /// image , QRcode , Signature, sticker , emoji
      id: data.id, /// local id for watermark
      tag: tag, /// z axis value
      rotation: data.deg, /// it will be in degree
      width: size.imageWidthOnCanvas, /// ratio value from 1 to 0 accoridng to canvas
      height: size.imageheightOnCanvas / size.imageWidthOnCanvas, /// ratio value from 1 to 0 accoridng to width
      opacity: data.opacity,
      scaleX: data.scaleX,
      scaleY: data.scaleY,
    };

    if (data.type === "image") {
      dataReturn = {
        ...dataReturn,
        //   image keys
        name: data.name, /// name format wm_image_unixtimestamp
        originalSize: data.size, /// watermark size for storage in bytes
        x: size.x / 100,
        y: size.y / 100,
      };
    }
    if (data.type === "text") {
      dataReturn = {
        ...dataReturn,
        //   label keys
        text: data.src, /// text string
        text_color: data.textColor, /// text color should be in HEX with #
        font: data.font, /// text fontb family name
        shadow_color:
          data.enableShadowColor || data.shadowColorEnable
            ? data.shadowColor
            : "", /// shadow color should be in HEX with # -- if this key is empty then shadow is not enable
        text_alignment: data.textAlign, /// text inner aligment left, right , center
        sizeOfContainer: data.sizeOfContainer,
        containerHight: data.containerHight,
        containerWidth: data.containerWidth,
        scaleFactor: data.scaleFactor,
        multiLine: data.multiLine,
        x: size.x / 100,
        y: size.y / 100,
      };
    }

    return dataReturn;
  },
  dataWrapperDecrypt(data, platform) {
    let dataReturn = {
      ...data,
      type: data.type,
      property: data.sub_type,
      id: data.id,
      deg: data.rotation,
      width: data.width,
      height: data.height,
      ScaleApi: true,
      scaleX: data.scaleX || 0,
      scaleY: data.scaleY || 0,
      platform,
      opacity: data.opacity,
    };

    const size = this.resetOffset(data.x, data.y, data.width, data.height);

    if (data.type === "image") {
      dataReturn = {
        ...dataReturn,
        //   image keys
        name: data.name, /// name format wm_image_unixtimestamp
        size: data.originalSize || data.src?.size, /// watermark size for storage in bytes
        x: data.x * 100,
        y: data.y * 100,
      };
    }
    if (data.type === "text") {
      dataReturn = {
        ...dataReturn,
        //   label keys
        src: data.text, /// text string
        textColor: data.text_color, /// text color should be in HEX with #
        font: data.font, /// text fontb family name
        shadowColor: data.shadow_color ? data.shadow_color : "#fff", /// shadow color should be in HEX with # -- if this key is empty then shadow is not enable
        shadowColorEnable: data.shadow_color ? true : false,
        textAlign: data.text_alignment, /// text inner aligment left, right , center
        enableShadowColor: data.shadow_color ? true : false,
        sizeOfContainer: data.sizeOfContainer,
        containerHight: data.containerHight,
        containerWidth: data.containerWidth,
        scaleFactor: data.scaleFactor,
        multiLine: data.multiLine,
        x: size.x * 100,
        y: size.y * 100,
      };
    }
    return dataReturn;
  },
  // Helper function to pad single-digit numbers with a leading zero
  unixTimestampToDateTime(unixTimestamp) {
    function pad(number) {
      return number < 10 ? "0" + number : number;
    }
    // Multiply by 1000 to convert from seconds to milliseconds
    const milliseconds = unixTimestamp * 1000;

    // Create a new Date object using the milliseconds
    const dateObject = new Date(milliseconds);

    // Extract the various components of the date
    const year = dateObject.getFullYear();
    const month = dateObject.getMonth() + 1; // Months are zero-based
    const day = dateObject.getDate();
    const hours = dateObject.getHours();
    const minutes = dateObject.getMinutes();
    const seconds = dateObject.getSeconds();

    // Format the date and time as a string
    const formattedDateTime = `${year}-${pad(month)}-${pad(day)} ${pad(
      hours
    )}:${pad(minutes)}:${pad(seconds)}`;

    return formattedDateTime;
  },
  resizeFile(file, width, height, Format, Quality) {
    const pica = new Pica();
    const img = new window.Image();
    const canvas = document.createElement("canvas");

    return new Promise((resolve, reject) => {
      img.onload = () => {
        canvas.width = width;
        canvas.height = height;

        pica
          .resize(img, canvas, {
            unsharpAmount: 80,
            unsharpRadius: 0.6,
            unsharpThreshold: 2,
            transferable: true,
          })
          .then((result) => {
            const dataURL = result.toDataURL(
              `image/${Format === "png" ? "png" : "jpeg"}`,
              Quality
            );
            resolve(dataURL);
          })
          .catch((error) => {
            reject(error);
          });
      };

      img.src = file;
    });
  },
};

export default helper_functions;
